// Les chiffres du tableau de bord — la lecture.
//
// Ce module ne fait que LIRE et assembler : factures, devis, chantiers et
// agenda sont lus une seule fois, puis tout le raisonnement est délégué à
// @imp/shared/tableau-bord, partagé et testé à part. Un écran qu'on ouvre
// vingt fois par jour ne doit pas déclencher vingt allers-retours.

import { calculerTableauBord, type ChiffresTableauBord, jourPlus } from '@imp/shared/tableau-bord';

import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';
import { ORG_ID } from '@/modules/clients/api';

export type { ChiffresTableauBord, Echeance } from '@imp/shared/tableau-bord';

interface FactureRow {
  id: string;
  numero: string;
  kind: string;
  statut: string;
  objet: string | null;
  date_emission: string;
  date_echeance: string | null;
  montant_ht: string | number | null;
  montant_ttc: string | number | null;
  montant_paye: string | number | null;
  facture_liee_id: string | null;
}

interface DevisRow {
  statut: string;
  date_emission: string;
  montant_ht: string | number | null;
}

interface ChantierRow {
  statut: string;
}

interface EvenementRow {
  id: string;
  titre: string;
  debut_at: string;
  journee_entiere: boolean;
  chantier_id: string | null;
}

/** Les montants arrivent en `numeric` : PostgREST les rend sous forme de texte. */
function nombre(valeur: string | number | null): number {
  if (valeur === null) return 0;
  const n = Number(String(valeur).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/** Le jour du calendrier où l'on se place, en date LOCALE. */
function jourLocal(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

export async function chargerDashboard(maintenant = new Date()): Promise<ChiffresTableauBord> {
  const jour = jourLocal(maintenant);
  // La semaine se lit du jour même à la fin du septième jour suivant.
  const debutSemaine = new Date(`${jour}T00:00:00`);
  const finSemaine = new Date(`${jourPlus(jour, 8)}T00:00:00`);

  // Tout est lu par tranches : ces tables couvrent l'historique complet et une
  // lecture tronquée fausserait les totaux SANS lever la moindre erreur.
  const [factures, devis, chantiers, evenements] = await Promise.all([
    lireTout<FactureRow>((de, a) =>
      supabase
        .from('factures')
        .select(
          'id,numero,kind,statut,objet,date_emission,date_echeance,montant_ht,montant_ttc,montant_paye,facture_liee_id',
        )
        .eq('organization_id', ORG_ID)
        .is('deleted_at', null)
        .order('id')
        .range(de, a),
    ),
    lireTout<DevisRow>((de, a) =>
      supabase
        .from('devis')
        .select('statut,date_emission,montant_ht')
        .eq('organization_id', ORG_ID)
        .is('deleted_at', null)
        .order('id')
        .range(de, a),
    ),
    lireTout<ChantierRow>((de, a) =>
      supabase
        .from('chantiers')
        .select('statut')
        .eq('organization_id', ORG_ID)
        .is('deleted_at', null)
        .order('id')
        .range(de, a),
    ),
    lireTout<EvenementRow>((de, a) =>
      supabase
        .from('evenements')
        .select('id,titre,debut_at,journee_entiere,chantier_id')
        .eq('organization_id', ORG_ID)
        .is('deleted_at', null)
        .gte('debut_at', debutSemaine.toISOString())
        .lt('debut_at', finSemaine.toISOString())
        .order('debut_at')
        .range(de, a),
    ),
  ]);

  return calculerTableauBord({
    jour,
    factures: factures.map((f) => ({
      id: f.id,
      numero: f.numero,
      kind: f.kind,
      statut: f.statut,
      objet: f.objet,
      dateEmission: f.date_emission,
      dateEcheance: f.date_echeance,
      montantHt: nombre(f.montant_ht),
      montantTtc: nombre(f.montant_ttc),
      montantPaye: nombre(f.montant_paye),
      factureLieeId: f.facture_liee_id,
    })),
    devis: devis.map((d) => ({
      statut: d.statut,
      dateEmission: d.date_emission,
      montantHt: nombre(d.montant_ht),
    })),
    statutsChantiers: chantiers.map((c) => c.statut),
    evenements: evenements.map((e) => ({
      id: e.id,
      titre: e.titre,
      debutAt: e.debut_at,
      journeeEntiere: e.journee_entiere,
      chantierId: e.chantier_id,
    })),
  });
}
